import React, { useState, useEffect } from "react";
import { Grid, FormControl, FormGroup, FormLabel, Box, FilledInput, TextField,InputAdornment } from "@material-ui/core";
import { get } from "../../utils/api-services";
import { Autocomplete } from "@material-ui/lab";

const DynamicForm = ({ formData, handleChange, config, fetchAvailableAcres, acresRefreshKey = 0 }) => {
    const [dropdownData, setDropdownData] = useState({});
    const [availableAcres, setAvailableAcres] = useState(0);
    const [filteredOptions, setFilteredOptions] = useState({});

    useEffect(() => {
        const fetchDropdowns = async () => {
            const dropdownEndpoints = config.fields.filter((field) => field.type === "dropdown");
            const dropdownPromises = dropdownEndpoints.map((field) =>
                get(field.endpoint).then((res) => ({ [field.name]: res.data }))
            );
            const results = await Promise.all(dropdownPromises);
            const dropdownValues = results.reduce((acc, curr) => ({ ...acc, ...curr }), {});
            setDropdownData(dropdownValues);
        };
        fetchDropdowns();
    }, [config]);

    useEffect(() => {
        if (config.fields.some(field => field.showAvailableAcres)) {
            const hasAreaField = config.fields.some((field) => field.name === "idfinca_area");
            if (config.idKey === "idfinca_area" && formData.idfinca_farm) {
                fetchAvailableAcres(formData.idfinca_farm, "farm")
                    .then(setAvailableAcres)
                    .catch(() => setAvailableAcres(0));
            } else if (config.idKey === "idfinca_section" && formData.idfinca_area) {
                fetchAvailableAcres(formData.idfinca_area, "area")
                    .then(setAvailableAcres)
                    .catch(() => setAvailableAcres(0));
            } else if (config.idKey === "idfinca_section" && !hasAreaField && formData.idfinca_farm) {
                fetchAvailableAcres(formData.idfinca_farm, "farm")
                    .then(setAvailableAcres)
                    .catch(() => setAvailableAcres(0));
            }
        }
    }, [formData.idfinca_farm, formData.idfinca_area, config, acresRefreshKey]);

    useEffect(() => {
        const filteredData = {};

        config.fields.forEach((field) => {
            if (field.dependsOn) {
                const parentValue = formData[field.dependsOn];
                if (parentValue && dropdownData[field.name]) {
                    filteredData[field.name] = dropdownData[field.name].filter(
                        (item) => item[field.dependsOn] === parentValue
                    );
                } else {
                    filteredData[field.name] = dropdownData[field.name] || [];
                }
            }
        });

        setFilteredOptions(filteredData);
    }, [formData, dropdownData]);

    useEffect(() => {
        if (formData.idfinca_farm) {
            handleChange({ target: { name: "idfinca_area", value: "" } });
            setAvailableAcres(0);
        }
    }, [formData.idfinca_farm]);

    useEffect(() => {
        if (formData.idfinca_area) {
            handleChange({ target: { name: "idfinca_section", value: "" } });
            setAvailableAcres(0);
        }
    }, [formData.idfinca_area]);

    return (
        <Grid container spacing={2}>
            {config.fields.map((field, index) => {
                const components = [];
                if (field.type === "text" || field.type === "number") {
                    components.push(
                        <FormField
                            key={field.name}
                            label={field.label}
                            name={field.name}
                            value={formData[field.name] || ""}
                            onChange={handleChange}
                            type={field.type}
                            rules={field.rules || {}}
                            disabled={field.disabled}
                            isCurrency={field.isCurrency ?? false}
                            filterRegex={
                                field.filterRegex || (
                                    config.idKey === "idfinca_product_type" && field.name === "name"
                                        ? /[^A-Za-z\/]/g
                                        : undefined
                                )
                            }
                        />
                    );
                }
                if (field.type === "dropdown") {
                    const options = field.dependsOn ? filteredOptions[field.name] || [] : dropdownData[field.name] || [];
                    components.push(
                        <DropdownField
                            key={field.name}
                            label={field.label}
                            name={field.name}
                            value={formData[field.name] || (field.multiple ? [] : "")}
                            options={options}
                            idKey={field.idKey}
                            nameKey={field.nameKey}
                            onChange={handleChange}
                            multiple={field.multiple || false}
                            disabled={field.disabled || false}
                        />
                    );
                }
                if (field.showAvailableAcres && formData[field.name]) {
                    components.push(
                        <Grid item xs={12} lg={6} key={`availableAcres-${index}`}>
                            <FormGroup>
                                <FormLabel>Available Acres</FormLabel>
                                <FormControl variant="filled" component={Box} width="100%">
                                    <FilledInput
                                        value={availableAcres || "0"}
                                        disabled
                                        style={{ backgroundColor: "#f5f5f5" }}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                    );
                }
                return components;
            })}
        </Grid>
    );
};

const FormField = ({ label, name, value, onChange, type, rules, filterRegex, isCurrency = false, disabled = false }) => {
    const isGeo = name === "latitude" || name === "longitude";
    const geoPattern = /^-?\d*\.?\d*$/; 
    const numPattern = /^\d*\.?\d*$/; 

    const handleInputChange = (e) => {
        let newValue = e.target.value;

        if (type === "number") {
            if (!(isGeo ? geoPattern : numPattern).test(newValue)) return;

            if (isGeo && (newValue === "-" || newValue === "-.")) {
                onChange({ target: { name, value: newValue } });
                return;
            }
            if (newValue === "") {
                onChange({ target: { name, value: "" } });
                return;
            }
            const numValue = Number(newValue);
            if (isGeo) {
                onChange({ target: { name, value: newValue } });
                return;
            } else {
                const minRule = rules?.min ?? 0.01;

                if (/^0[0-9]+/.test(newValue)) newValue = String(Number(newValue));
                if (numValue < minRule && numValue !== 0) {
                onChange({ target: { name, value: "" } });
                return;
                }
                onChange({ target: { name, value: newValue } });
                return;
            }
        } else if (type === "text" && filterRegex) {
            newValue = newValue.replace(filterRegex, "");
            onChange({ target: { name, value: newValue } });
            return;
        } else {
            onChange(e);
        }
    };

    return (
        <Grid item xs={12} lg={6}>
            <FormGroup>
                <FormLabel>{label}</FormLabel>
                <FormControl variant="filled" component={Box} width="100%">
                    <FilledInput
                        autoComplete="off"
                        name={name}
                        value={value}
                        onChange={handleInputChange}
                        onBlur={(e) => {
                            if (type !== "number") return;
                            const raw = e.target.value;

                            if (raw === "" || (isGeo && (raw === "-" || raw === "-."))) {
                                onChange({ target: { name, value: "" } });
                                return;
                            }
                           let num = Number(raw);
                            if (!Number.isFinite(num)) {
                                onChange({ target: { name, value: "" } });
                                return;
                            }

                            if (isGeo) {
                                const [min, max] = name === "latitude" ? [-90, 90] : [-180, 180];
                                if (num < min) num = min;
                                if (num > max) num = max;
                                onChange({ target: { name, value: String(num) } });
                            } else {
                                const minRule = rules?.min ?? 0.01;
                                if (num === 0 && minRule > 0) {
                                onChange({ target: { name, value: "" } });
                                } else {
                                onChange({ target: { name, value: String(num) } });
                                }
                            }
                        }}
                        type={type}
                        inputProps={rules}
                        disabled={disabled}
                        placeholder={label}
                        startAdornment={
                            isCurrency ? (
                                <InputAdornment position="start">$</InputAdornment>
                            ) : null
                        }
                    />
                </FormControl>
            </FormGroup>
        </Grid>
    );
}

const DropdownField = ({
    label,
    name,
    value,
    options,
    idKey,
    nameKey,
    onChange,
    multiple = false,
    disabled = false,
}) => (
    <Grid item xs={12} lg={6}>
        <FormGroup>
            <FormLabel>{label}</FormLabel>
            <Autocomplete
                multiple={multiple}
                options={options}
                getOptionLabel={(option) => option[nameKey] || ""}
                value={
                    multiple
                        ? options.filter((option) => value?.includes(option[idKey]))
                        : options.find((option) => option[idKey] === value) || null
                }
                onChange={(event, newValue) => {
                    const newVal = multiple
                        ? newValue.map((item) => item[idKey])
                        : newValue
                            ? newValue[idKey]
                            : "";
                    onChange({
                        target: {
                            name,
                            value: newVal,
                        },
                    });
                }}
                disabled={disabled}
                size="small"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="filled"
                        placeholder={`Select ${label}`}
                        fullWidth
                        InputProps={{
                            ...params.InputProps,
                            style: {
                                height: "100%",
                                padding: "0",
                                paddingLeft: "0.5rem",
                            },
                        }}
                    />
                )}
                style={{ width: "100%" }}
            />
        </FormGroup>
    </Grid>
);

export default DynamicForm;